// Review → Gates (design §14): the five gates, each with its tasks, lessons learned and the next gate's steps. A gate
// closes when every task is done and the lessons are written. Internal, except where a row says the firm sees it:
// tasks given to the firm (its status page lists them while their gate is open) and step ticks (its status page).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, Trash2 } from 'lucide-react';
import {
  GATE_HOURS_MAX, LESSONS_MAX, nextGate, TASK_TITLE_MAX,
  type AddTaskRequest, type DiscoveryNote, type GateId, type GateTaskView, type GateView, type Review, type UpdateTaskRequest,
} from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName, fmtDateTime, fmtMonthDay, plural } from '../lib/format';
import { Saver, type SaveStatus } from '../intake/saver';
import { Banner, Button, Dialog, Pill, Select, TextField, cx } from '../ui';
import { useMe } from './AdminShell';
import { MessageBody } from './feedback/Formatting';
import { FormattedNote } from './FormattedNote';

type Person = { id: string; name: string };
type Run = (fn: () => Promise<unknown>) => Promise<boolean>;
const today = () => new Date().toISOString().slice(0, 10);

export function GatesTab({ r, reload }: { r: Review; reload: () => Promise<void> }) {
  const open = r.gates.find((g) => g.state === 'open');
  const [selected, setSelected] = useState<GateId>(open?.id ?? 'succeed');
  const [team, setTeam] = useState<Person[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.get<{ team: Person[] }>('/api/admin/work/team').then((x) => setTeam(x.team)).catch(() => setTeam([])); }, []);
  // Closing a gate moves the view on to the next one.
  useEffect(() => { if (open) setSelected(open.id); }, [open?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run: Run = async (fn) => {
    setErr(null);
    try { await fn(); await reload(); return true; }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That change did not save.'); return false; }
  };
  const g = r.gates.find((x) => x.id === selected);
  if (!g) return null;
  const i = r.gates.indexOf(g);
  const prev = i > 0 ? r.gates[i - 1]! : null;
  const after = nextGate(g.id) ? r.gates[i + 1]! : null;
  const editable = r.status !== 'withdrawn';
  const firm = r.header.contact?.name || r.header.inviteName;
  const base = `/api/admin/onboardings/${r.id}`;
  const lastClosed = [...r.gates].reverse().find((x) => x.state === 'closed');

  return (
    <div className="grid items-start gap-6 md:grid-cols-[230px_minmax(0,1fr)]">
      <nav aria-label="Gates" className="flex flex-col gap-0.5">
        {r.gates.map((x, n) => (
          <button key={x.id} type="button" aria-current={x.id === selected ? 'true' : undefined} onClick={() => setSelected(x.id)}
            className={cx('flex items-start gap-2.5 rounded-[10px] border-0 bg-transparent px-3 py-2.5 text-left hover:bg-panel', x.id === selected && '!bg-brand-tint shadow-[inset_3px_0_0_rgb(var(--brand))]')}>
            <GateDot state={x.state} />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{x.title}</span>
              <span className="block text-[12.5px] text-muted">{railLine(x, r.gates[n - 1])}</span>
            </span>
          </button>
        ))}
      </nav>

      <section aria-labelledby="gate-h" className="flex min-w-0 flex-col">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id="gate-h" className="m-0 text-lg font-bold">{g.title}</h3>
          {g.state === 'closed' && (
            <span className="text-[13px] text-muted">
              Closed {fmtDateTime(g.closedAt!)}{g.closedBy ? ` by ${g.closedBy}` : ''}
              {editable && lastClosed?.id === g.id && <> · <Button variant="link" size="sm" onClick={() => void run(() => api.post(`${base}/gates/${g.id}/reopen`, {}))}>Reopen</Button></>}
            </span>
          )}
          {g.state === 'open' && editable && (
            <Button variant={g.blockers.length ? 'secondary' : 'primary'} size="sm"
              onClick={() => { document.getElementById('gate-close')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); document.getElementById('gate-hours')?.focus({ preventScroll: true }); }}>
              Close {g.title}{g.blockers.length ? ` · ${g.blockers[0]}` : ''}
            </Button>
          )}
          {g.state === 'open' && prev?.state === 'closed' && (
            <span className="text-[13px] text-muted">
              {prev.title} closed {fmtMonthDay(prev.closedAt!)}{prev.closedBy ? ` by ${prev.closedBy}` : ''} · <Button variant="link" size="sm" onClick={() => setSelected(prev.id)}>See its close-out</Button>
            </span>
          )}
        </div>
        <p className="mb-1 mt-0.5 text-[13px] text-muted">
          {g.state === 'later' ? `Opens when the ${prev?.title} gate closes. You can line up its tasks now.` : 'Internal. The firm sees only the tasks you give it.'}
        </p>
        {err && <Banner tone="error" role="alert" className="mt-2">{err}</Banner>}

        <Part n={1} title="Tasks" hint={g.state === 'closed' ? undefined : 'Filled in for every firm. Add your own. Tasks marked “ticks itself” are done when the app sees it happen.'}>
          <TaskTable r={r} gate={g} team={team} run={run} editable={editable && g.state !== 'closed'} firm={firm} addLabel="Add a task" />
        </Part>

        <Part n={2} title="Lessons learned" hint={g.state === 'closed' ? undefined : 'What we would do differently for the next firm. Staff only.'}>
          {g.state === 'closed'
            ? g.lessons ? <MessageBody text={g.lessons.body} className="rounded-[10px] border border-line bg-row-hi px-3 py-2.5 text-sm" />
              : <p className="m-0 text-sm text-muted">None. This gate closed before gates had lessons learned.</p>
            : <Lessons key={g.id} onboardingId={r.id} gate={g.id} lessons={g.lessons} readOnly={!editable} onSaved={() => void reload()} />}
        </Part>

        {g.state === 'open' && after && (
          <Part n={3} title={`Next steps · the ${after.title} gate’s tasks`}
            hint={`Outline everything that happens next. When you close this gate, these become the ${after.title} gate’s tasks.`}>
            <TaskTable r={r} gate={after} team={team} run={run} editable={editable} firm={firm} addLabel="Add a step" />
          </Part>
        )}

        {g.state === 'open' && editable && <CloseBar r={r} gate={g} after={after} run={run} />}
      </section>
    </div>
  );
}

function railLine(g: GateView, prev: GateView | undefined): string {
  if (g.state === 'closed') return `Closed ${fmtMonthDay(g.closedAt!)}${g.closedBy ? ` · ${g.closedBy}` : ''}`;
  if (g.state === 'open') return `Open · ${g.tasks.filter((t) => t.done).length} of ${g.tasks.length} tasks done`;
  return prev?.state === 'open' ? `${plural(g.tasks.length, 'task')} lined up` : 'Later';
}

function GateDot({ state }: { state: GateView['state'] }) {
  if (state === 'closed') return <span aria-hidden className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-ok"><Check size={12} strokeWidth={3.5} className="text-white" /></span>;
  return <span aria-hidden className={cx('mt-0.5 h-5 w-5 flex-none rounded-full border-2', state === 'open' ? 'border-brand bg-brand shadow-[inset_0_0_0_4px_white]' : 'border-line-btn')} />;
}

function Part({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="border-t border-line-soft py-3.5 first-of-type:mt-3">
      <h4 className="m-0 flex items-center gap-2 text-[15px] font-semibold">
        <span aria-hidden className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand">{n}</span>{title}
      </h4>
      {hint && <p className="mb-2 ml-[30px] mt-0.5 text-[13px] text-muted">{hint}</p>}
      <div className="ml-[30px] mt-1.5">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------- tasks

function TaskTable({ r, gate, team, run, editable, firm, addLabel }: {
  r: Review; gate: GateView; team: Person[]; run: Run; editable: boolean; firm: string; addLabel: string;
}) {
  const base = `/api/admin/onboardings/${r.id}`;
  const [toFirm, setToFirm] = useState<GateTaskView | null>(null);
  // A tick shows at once; the reload after the save brings the server's state (and drops this).
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const patch = (t: GateTaskView, b: UpdateTaskRequest) => run(() => api.patch(`${base}/tasks/${t.id}`, b));
  const tick = async (t: GateTaskView, done: boolean) => {
    setTicked((x) => ({ ...x, [t.id]: done }));
    await patch(t, { done });
    setTicked(({ [t.id]: _, ...rest }) => rest);
  };
  const ownerValue = (t: GateTaskView) => (t.owner.kind === 'firm' ? 'firm' : t.owner.id);
  const setOwner = (t: GateTaskView, v: string) => {
    // A task added by hand that the firm will own goes on its status page: say so before it does.
    if (v === 'firm' && !t.standard && t.owner.kind !== 'firm') { setToFirm(t); return; }
    void patch(t, { owner: v === 'firm' ? { kind: 'firm' } : { kind: 'staff', staffId: v } });
  };
  return (
    <div className="flex flex-col gap-1">
      <ul className="m-0 flex list-none flex-col p-0" aria-label={`${gate.title} tasks`}>
        {gate.tasks.map((t) => {
          const overdue = !t.done && !!t.dueDate && t.dueDate < today();
          return (
            <li key={t.id} className={cx('flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line-soft py-2 last:border-b-0', !t.standard && 'bg-row-hi')}>
              <input type="checkbox" className="h-[18px] w-[18px] flex-none accent-ok" aria-label={`Done: ${t.title}`} checked={ticked[t.id] ?? t.done}
                disabled={!editable || t.kind === 'auto'} title={t.kind === 'auto' ? 'Ticks itself when the app sees it happen' : undefined}
                onChange={(e) => void tick(t, e.target.checked)} />
              <div className="min-w-0 flex-[1_1_220px]">
                <div className={cx('text-sm', t.done && 'text-muted line-through')}>{t.title}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted">
                  {t.kind === 'auto' && <span className={cx(!t.done && t.detail?.startsWith('No note') && 'text-warn')}>Ticks itself{t.detail ? ` · ${t.detail}` : ''}</span>}
                  {t.addedBy && <span>Added by {t.addedBy}</span>}
                  {t.done && t.doneAt && <span>Done {fmtMonthDay(t.doneAt)}{t.doneBy ? ` · ${firstName(t.doneBy)}` : ''}</span>}
                  {t.firmSees === 'task' && <Pill tone="warn" className="!px-2 !py-0 !text-xs">Firm sees this</Pill>}
                  {t.firmSees === 'tick' && <Pill tone="info" className="!px-2 !py-0 !text-xs">Firm sees the tick</Pill>}
                </div>
              </div>
              <Select small aria-label={`Owner of ${t.title}`} className="w-[190px]" value={ownerValue(t)} disabled={!editable} onChange={(e) => setOwner(t, e.target.value)}>
                {t.owner.kind === 'staff' && !team.some((p) => t.owner.kind === 'staff' && p.id === t.owner.id) && <option value={t.owner.id}>{t.owner.name}</option>}
                {team.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                <option value="firm">Firm · {firm}</option>
              </Select>
              <input type="date" aria-label={`Due date of ${t.title}`} value={t.dueDate ?? ''} disabled={!editable}
                onChange={(e) => void patch(t, { dueDate: e.target.value || null })}
                className={cx('h-9 w-[150px] rounded-[10px] border border-line-strong bg-white px-2 text-sm', overdue && '!border-bad text-bad')} />
              <span className="w-9 flex-none">
                {editable && !t.standard && (
                  <Button variant="quiet" size="sm" aria-label={`Remove ${t.title}`} className="!w-9 !px-0" onClick={() => void run(() => api.del(`${base}/tasks/${t.id}`))}>
                    <Trash2 aria-hidden size={15} />
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {editable && <AddTask r={r} gate={gate} team={team} run={run} firm={firm} label={addLabel} />}
      <Dialog open={!!toFirm} title="Give this task to the firm?" onClose={() => setToFirm(null)}
        footer={<>
          <Button onClick={() => setToFirm(null)}>Cancel</Button>
          <Button variant="primary" onClick={() => { const t = toFirm!; setToFirm(null); void patch(t, { owner: { kind: 'firm' } }); }}>Give it to the firm</Button>
        </>}>
        <p className="m-0 text-sm">“{toFirm?.title}” will show on {r.header.firmName}’s status page under Your to-dos{gate.state === 'open' ? '' : ` once the ${gate.title} gate opens`}. No email is sent.</p>
      </Dialog>
    </div>
  );
}

function AddTask({ r, gate, team, run, firm, label }: { r: Review; gate: GateView; team: Person[]; run: Run; firm: string; label: string }) {
  const { me } = useMe();
  const mine = useMemo(() => team.find((p) => p.name === me?.name)?.id ?? team[0]?.id ?? 'firm', [team, me?.name]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const who = owner || mine;
  if (!adding) {
    return <Button variant="link" size="sm" className="self-start" onClick={() => setAdding(true)}>+ {label}</Button>;
  }
  const add = async () => {
    const body: AddTaskRequest = { title: title.trim(), owner: who === 'firm' ? { kind: 'firm' } : { kind: 'staff', staffId: who }, dueDate: due || null };
    if (await run(() => api.post(`/api/admin/onboardings/${r.id}/gates/${gate.id}/tasks`, body))) { setTitle(''); setDue(''); setOwner(''); setAdding(false); }
  };
  return (
    <form className="mt-1.5 flex flex-col gap-2 rounded-[10px] border border-line p-3" onSubmit={(e) => { e.preventDefault(); void add(); }}>
      <div className="flex flex-wrap items-end gap-2">
        <TextField label={gate.state === 'open' ? 'Task' : 'Step'} autoFocus maxLength={TASK_TITLE_MAX} value={title} onChange={(e) => setTitle(e.target.value)} className="min-w-[220px] flex-1" />
        <Select label="Owner" className="w-[200px]" value={who} onChange={(e) => setOwner(e.target.value)}>
          {team.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          <option value="firm">Firm · {firm}</option>
        </Select>
        <TextField label="Due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-[160px]" />
      </div>
      {who === 'firm' && (
        <p className="m-0 text-[13px] font-semibold text-warn">
          Shows on {r.header.firmName}’s status page under Your to-dos{gate.state === 'open' ? '' : ` once the ${gate.title} gate opens`}. No email is sent.
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={!title.trim()}>Add</Button>
        <Button size="sm" onClick={() => setAdding(false)}>Cancel</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- lessons and close

function Lessons({ onboardingId, gate, lessons, readOnly, onSaved }: {
  onboardingId: string; gate: GateId; lessons: DiscoveryNote | null; readOnly: boolean; onSaved: () => void;
}) {
  const [text, setText] = useState(lessons?.body ?? '');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [fatal, setFatal] = useState<string | null>(null);
  const saver = useMemo(() => new Saver((e) => setFatal(e.message)), []);
  useEffect(() => {
    const off = saver.subscribe(setStatus);
    return () => { off(); void saver.flush(); };
  }, [saver]);
  if (readOnly) return lessons ? <MessageBody text={lessons.body} className="text-sm" /> : <p className="m-0 text-sm text-muted">None.</p>;
  const change = (body: string) => {
    setText(body);
    setFatal(null);
    saver.schedule(gate, async () => { await api.put(`/api/admin/onboardings/${onboardingId}/gates/${gate}/lessons`, { body }); onSaved(); }, 800);
  };
  const state = fatal ?? (status === 'saving' ? 'Saving…' : status === 'error' ? 'Couldn’t save. Retrying…'
    : lessons ? `Saved by ${firstName(lessons.by)}, ${fmtMonthDay(lessons.at)}` : '');
  return (
    <div className="flex flex-col gap-1">
      <FormattedNote label={<span className="sr-only">Lessons learned</span>} name="lessons learned" value={text} onChange={change} onDone={() => void saver.flush()} rows={3} maxLength={LESSONS_MAX}
        placeholder="What we would do differently for the next firm" />
      <div aria-live="polite" className={cx('min-h-4 text-[12px]', fatal || status === 'error' ? 'font-semibold text-bad' : 'text-muted')}>{state}</div>
    </div>
  );
}

function CloseBar({ r, gate, after, run }: { r: Review; gate: GateView; after: GateView | null; run: Run }) {
  const [hours, setHours] = useState('');
  const h = Number(hours);
  const badHours = hours.trim() !== '' && !(h > 0 && h <= GATE_HOURS_MAX);
  const ready = gate.blockers.length === 0;
  const effect = gate.id === 'discovery'
    ? `Closing locks the firm’s answers, lets you send the agreement and opens the ${after?.title} gate.`
    : after ? `Closing opens the ${after.title} gate.` : 'This is the last gate.';
  const close = () => run(() => api.post(`/api/admin/onboardings/${r.id}/gates/${gate.id}/close`, { hours: hours.trim() ? h : null }));
  return (
    <div id="gate-close" className="flex flex-col gap-3 border-t border-line pt-3.5">
      <label className="flex flex-wrap items-center gap-2.5 text-sm">
        Hours spent on this gate
        <input id="gate-hours" type="number" min="0" step="0.25" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)}
          className={cx('h-9 w-[90px] rounded-[10px] border border-line-strong px-2.5 tabular-nums', badHours && 'input-invalid')} />
        <span className="text-[13px] text-muted">Optional. Adds to onboarding hours on the KPI page when you close.</span>
      </label>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-[13.5px] text-ink-2">
          {r.header.submittedAt === null && <b className="text-warn">The firm hasn’t submitted its questionnaire yet. </b>}
          {!ready && <b className="text-warn">{capital(gate.blockers.join(', '))}. </b>}{effect}
        </p>
        <Button variant="primary" disabled={!ready || badHours || r.header.submittedAt === null} onClick={() => void close()}>Close {gate.title}</Button>
      </div>
    </div>
  );
}
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
